import { getStatusCssClass as _getStatusCssClass, getStatusLabel as _getStatusLabel } from '../../../shared/utils/statusConstants';

export const parseDateStr = (dateStr) => {
    if (!dateStr || dateStr === 'N/A' || dateStr === 'Not Specified') return null;
    let cleanStr = String(dateStr).trim();

    if (/\d{4}-\d{2}-\d{2}/.test(cleanStr)) {
        const d = new Date(cleanStr);
        return isNaN(d.getTime()) ? null : d;
    }

    // Attempt to handle DD-MM-YYYY or DD/MM/YYYY
    const dmyMatch = cleanStr.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
    if (dmyMatch) {
        cleanStr = `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`;
    } else {
        // remove ordinals like "13th", "1st"
        cleanStr = cleanStr.replace(/(\d+)(st|nd|rd|th)/g, '$1');
        const yearMatch = cleanStr.match(/\b\d{4}\b/);
        if (yearMatch) {
            const yearIndex = cleanStr.indexOf(yearMatch[0]);
            cleanStr = cleanStr.substring(0, yearIndex + 4);
        } else {
            cleanStr = cleanStr.split(',')[0].trim();
        }
    }

    const d = new Date(cleanStr);
    return isNaN(d.getTime()) ? null : d;
};

// Returns true if dateStr is strictly before today (exceeded timeline)
export const isExceededTimeline = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(d);
    due.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
};

// Returns true if dateStr is today or within the rest of this week (Mon–Sun)
export const isDueSoon = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(d);
    due.setHours(0, 0, 0, 0);
    if (due.getTime() < today.getTime()) return false; // past → exceeded, not due soon
    // End of current week (Sunday)
    const endOfWeek = new Date(today);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon ... 6=Sat
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    endOfWeek.setDate(today.getDate() + daysUntilSunday);
    endOfWeek.setHours(23, 59, 59, 999);
    return due.getTime() <= endOfWeek.getTime();
};

export const formatDisplayDate = (dateStr) => {
    const d = parseDateStr(dateStr);
    if (!d) return dateStr || 'N/A';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
};

export const formatFullTimestamp = (dateStr) => {
    if (!dateStr) return 'N/A';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const strHours = String(hours).padStart(2, '0');

    return `${day}/${month}/${year} ${strHours}:${minutes} ${ampm}`;
};

export const getStatusValue = (prop) => {
    return prop.proposal_stage || prop.global_status?.label || prop.status || prop.rfp_status || prop.extraction_data?.status || '';
};

export const getStatusClass = _getStatusCssClass;
export const getStatusLabel = _getStatusLabel;

export const capitalize = (str) =>
    String(str).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const ITEMS_PER_PAGE = 9;

// Helper to determine if a proposal is expired (by status code 10, status text, or exceeded timeline)
export const isProposalExpired = (rfp) => {
    if (!rfp) return false;
    const code = Number(rfp.global_status?.code ?? rfp.status_code ?? rfp.status ?? rfp.proposal_stage);
    if (code === 10 || code === 56) return true;
    const stageStr = String(rfp.proposal_stage || rfp.global_status?.label || rfp.status || '').toLowerCase();
    if (stageStr.includes('expire')) return true;
    const isFinal = stageStr.includes('submit') || stageStr.includes('accept') || stageStr.includes('reject');
    const deadline = rfp.proposal_deadline || rfp.extraction_data?.submission_deadline;
    if (!isFinal && deadline && isExceededTimeline(deadline)) return true;
    return false;
};

export const statusLabels = {
    all: 'All',
    review: 'In Progress',
    submitted: 'Submitted',
    finalised: 'Finalised',
    accepted: 'Accepted',
    rejected: 'Rejected',
    go: 'Go',
    nogo: 'No-Go',
    pending: 'Decision Pending',
    expired: 'Expired'
};

export const mainStatusKeys = ['all', 'review', 'submitted', 'finalised'];
export const moreStatusKeys = ['accepted', 'rejected', 'go', 'nogo', 'pending', 'expired'];

export const statusToStageCode = {
    all: null,
    review: 2,        // Code 2: In-Progress
    submitted: 5,     // Code 5: Submitted to Client
    finalised: 4,     // Code 4: Proposal Finalised
    accepted: 6,      // Code 6: Accepted
    rejected: 7,      // Code 7: Rejected
    go: 8,            // Code 8: Go
    nogo: 9,          // Code 9: No-Go
    pending: 12,      // Code 12: Decision Pending
    expired: 10       // Code 10: RFP Proposal Expired
};

